import { DateRangePicker } from 'react-date-range';
import { enGB } from 'date-fns/locale';
import { Button, Spin } from 'antd';
import SelectedDatesPanel from './components/SelectedDatesPanel';
import React, { useEffect, useMemo, useState } from 'react';
import {
  getAllDatesNotEqualToWeekday,
  getDatesBetween,
  getDatesInMonthAfterDate,
  getDatesInMonthBeforeDate,
  getNextWeekday,
  normalizeTZOffset,
  toNonUTCDate,
  toUTCDate,
} from '../../utils/date';
import moment from 'moment-timezone';
import AvailabilityDate from './components/AvailabilityDate';
import './assets/css/calendar.css';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css';
import { addDays } from 'date-fns';

export const emptyDatesObject = {
  startDate: null,
  endDate: addDays(null, null),
  key: 'selection',
  end_date: null,
};

/**
 * Get the list of current disabled dates based on the current calendar selection.
 * <br/>
 * For example: prevents selecting arrival-only dates if a departure date is
 * currently being selected, and prevents selecting a period which overlaps a
 * limited-availability date, and so on...
 *
 * @param fullyBookedDates the list of fully-booked/unavailable dates
 * @param limitedAvailabilityDates the list of limited availability objects
 * @param startDate the currently selected booking start date object
 * @param endDate the currently selected booking end date object
 * @param maxSelectableDate the maximum selectable date
 * @param maxVisibleDate the maximum visible date
 */
function getDynamicDisabledDates(
  fullyBookedDates,
  limitedAvailabilityDates,
  startDate,
  endDate,
  maxSelectableDate,
  maxVisibleDate
) {
  limitedAvailabilityDates = limitedAvailabilityDates.sort(
    (a, b) => moment(a.date).unix() - moment(b.date).unix()
  );
  const start = moment(normalizeTZOffset(startDate));
  const end = moment(normalizeTZOffset(endDate));

  let disabledDates = [...fullyBookedDates];

  if (maxSelectableDate && maxVisibleDate) {
    disabledDates.push(...getDatesBetween(maxSelectableDate, maxVisibleDate));
  }

  // A start date (arrival) is selected but not an end-date (departure)
  const isSelectingDeparture =
    start.isValid() && (!end.isValid() || start.isSame(end, 'day'));

  if (!isSelectingDeparture) {
    // Selecting Arrival date -- disable all departure-only dates
    disabledDates.push(
      ...limitedAvailabilityDates
        .filter((ld) => ld.type === 'departure-only')
        .map((ld) => ld.date)
    );
  } else {
    // Dates which can't be selected as a valid departure date (or arrival date if they're in the past)
    const invalidSecondSelectionDates = limitedAvailabilityDates.filter(
      (ld) =>
        (ld.type === 'arrival-only' && moment(ld.date).isAfter(start, 'day')) ||
        ((ld.type === 'departure-only' || ld.type === 'no-night') &&
          moment(ld.date).isBefore(start, 'day'))
    );
    disabledDates.push(...invalidSecondSelectionDates.map((ld) => ld.date));

    const blockingDates = [
      ...fullyBookedDates,
      ...limitedAvailabilityDates.map((d) => d.date),
    ].sort((a, b) => moment(a).unix() - moment(b).unix());

    // Block all dates after the closest future date which has an un-bookable period within it
    const isSelectedAFutureBlocker = limitedAvailabilityDates.some(
      ({ date, type }) => start.isSame(date, 'day') && type === 'no-night'
    );
    const closestFutureBlocker = isSelectedAFutureBlocker
      ? moment(startDate).add(1, 'day').toDate()
      : blockingDates.find((d) => moment(d).isAfter(start, 'day'));
    if (closestFutureBlocker) {
      disabledDates.push(...getDatesInMonthAfterDate(closestFutureBlocker));
    }

    // Block all dates before the closest past date which has an un-bookable period within it
    const isSelectedAPastBlocker = limitedAvailabilityDates.some(
      ({ date, type }) => start.isSame(date, 'day') && type === 'arrival-only'
    );
    const closestPastBlocker = isSelectedAPastBlocker
      ? startDate
      : blockingDates
          .slice()
          .reverse()
          .find((d) => moment(d).isBefore(start, 'day'));
    if (closestPastBlocker) {
      disabledDates.push(...getDatesInMonthBeforeDate(closestPastBlocker));
    }

    // If the selected date has no-night availability, block all future dates
    if (
      limitedAvailabilityDates.some(
        ({ date, type }) => start.isSame(date, 'day') && type === 'no-night'
      )
    ) {
      disabledDates.push(start.clone().add(1, 'day').toDate());
    }
  }
  return disabledDates.map(toNonUTCDate);
}

/**
 * Get the list of current disabled dates if there is a selected package. Does not
 * have support for limited availability due to the more strict nature of custom
 * packages.
 * <br/>
 * Disables all non-arrival dates and all fully-booked dates
 *
 * @param fullyBookedDates the list of fully-booked/unavailable dates
 * @param selectedPackage the currently selected package object
 * @param minDate the calendar minDate
 * @param maxDate the calendar maxDate
 */
function getCustomPackageDisabledDates(
  fullyBookedDates,
  selectedPackage,
  minDate,
  maxDate
) {
  let disabledDates = [...fullyBookedDates];
  let dummyMaxDate = new Date();

  dummyMaxDate.setFullYear(dummyMaxDate.getFullYear() + 2);
  let max_Date = maxDate ? maxDate : dummyMaxDate;
  const arrivalDay = selectedPackage?.arrival_day;
  if (arrivalDay) {
    const nonArrivalDays = getAllDatesNotEqualToWeekday(
      minDate,
      max_Date,
      arrivalDay
    );

    disabledDates.push(...nonArrivalDays.map(toUTCDate));
  }
  return disabledDates.map(toNonUTCDate);
}

function AvailabilityCalendar({
  fullyBookedDates,
  limitedAvailabilityDates,
  selectedPackage = null,
  selectedRanges, // Seemingly needs to be passed as an invariant array reference to prevent erroneous re-rendering
  minDate = new Date(),
  maxDate = null,
  maxSelectableDate = null,
  maxDateSelectMessage = 'Bookings this far in advance are not available yet. Please select different dates.',
  loading = false,
  showSelectedDate = true,
  onDateSelection = () => {},
  onMonthChange = () => {},
  clearSelectedDates = () => {},
  reRenderKey = '',
  activeLanguage,
  showSelectionTail = false,
  isUnitTestEnvironment = false,
}) {
  maxDate = maxDate ?? maxSelectableDate;
  const [focusedRange, setFocusedRange] = useState([0, 0]);
  const minDateCorrected = minDate
    ? normalizeTZOffset(moment(minDate).format('YYYY-MM-DD'))
    : null;
  const maxDateCorrected = maxDate ? normalizeTZOffset(moment(maxDate)) : null;
  const maxSelectableDateCorrected = maxSelectableDate
    ? normalizeTZOffset(moment(maxSelectableDate))
    : null;

  const { startDate = null, endDate = null } = selectedRanges?.[0];
  useEffect(() => {
    if (!startDate) {
      setFocusedRange([0, 0]);
    }
  }, [startDate, endDate]);
  const maxDateToShow = new Date();
  maxDateToShow.setFullYear(maxDateToShow.getFullYear() + 5);
  const disabledDates = useMemo(() => {
    if (
      selectedPackage &&
      selectedPackage?.arrival_day &&
      selectedPackage?.departure_day
    ) {
      return getCustomPackageDisabledDates(
        fullyBookedDates,
        selectedPackage,
        minDate,
        maxDate
      );
    } else {
      return getDynamicDisabledDates(
        fullyBookedDates,
        limitedAvailabilityDates,
        startDate,
        endDate,
        maxSelectableDateCorrected,
        maxDateCorrected
      );
    }
  }, [
    selectedPackage,
    fullyBookedDates,
    minDate,
    maxDate,
    limitedAvailabilityDates,
    startDate,
    endDate,
    maxSelectableDateCorrected,
    maxDateCorrected,
  ]);

  return (
    <>
      <div
        className={
          (loading ? 'calendar-show-loading ' : '') +
          'angler-web-calendar gLight ' +
          (showSelectionTail ? 'angler-web-calendar--with-tail ' : '')
        }
      >
        {loading && (
          <div id='booking-calendar-loading'>
            <Spin />
          </div>
        )}
        <DateRangePicker
          key={reRenderKey}
          onChange={(item) => {
            const selection = item.selection;

            if (!selection.startDate) {
              // Needed for the clear selection button -- seems to be an issue in the library where sometimes it
              //   interprets a null startDate as valid and only returns a selected endDate
              selection.startDate = selection.endDate;
            }

            // If a package has been selected, set endDate to the correct departure day
            const fixedDepartureDay = selectedPackage?.departure_day;
            if (
              selectedPackage &&
              !selectedPackage?.arrival_day &&
              !selectedPackage?.departure_day
            ) {
              let currDate = new Date(selection.startDate);
              currDate.setDate(
                currDate.getDate() + selectedPackage?.booking_days
              );
              selection.endDate = currDate;
            } else if (fixedDepartureDay) {
              selection.endDate = getNextWeekday(
                selection.startDate,
                fixedDepartureDay,
                selectedPackage?.arrival_day === selectedPackage?.departure_day,
                selectedPackage?.booking_week
              );
            }

            // This is to render in time selection page
            selection['end_date'] = selection['endDate'];
            onDateSelection(item.selection);
          }}
          onShownDateChange={(date) => onMonthChange(date)}
          disabledDates={disabledDates}
          locale={enGB}
          months={1}
          ranges={selectedRanges}
          dragSelectionEnabled={
            /*
               Weird bug with library where disabling this breaks functionality in unit tests, but it needs to be
               disabled for solely visual animation purposes (not functionality)
             */
            isUnitTestEnvironment
          }
          showPreview={false}
          direction='horizontal'
          color='black'
          rangeColors={['#60B077', 'white']}
          maxDate={maxDateToShow}
          minDate={minDateCorrected}
          {...(maxDate
            ? {
                maxDate: maxDateCorrected,
              }
            : {})}
          focusedRange={focusedRange}
          onRangeFocusChange={setFocusedRange}
          dayContentRenderer={(date) => (
            <AvailabilityDate
              date={date}
              utcDate={toUTCDate(date)}
              fullyBookedDates={fullyBookedDates}
              limitedAvailabilityDates={limitedAvailabilityDates}
              disabledDates={disabledDates.map(toUTCDate)}
              minDate={minDateCorrected}
              maxDate={maxDateCorrected}
              maxSelectableDate={maxSelectableDateCorrected}
              maxSelectableDateMessage={maxDateSelectMessage}
              selectedPackage={selectedPackage}
              selectedBookingDates={selectedRanges?.[0]}
              clearSelectedDates={() => {
                if (!selectedRanges?.[0]?.startDate) {
                  return;
                }
                if (
                  moment(selectedRanges[0].startDate).isSame(
                    selectedRanges?.[0]?.endDate,
                    'day'
                  )
                ) {
                  clearSelectedDates();
                  setFocusedRange([0, 0]);
                }
              }}
            />
          )}
        />
      </div>
      <Button
        type={'text'}
        className={'w-full txt-center mg-top-5 mg-bottom-8'}
        onClick={() => {
          clearSelectedDates();
          setFocusedRange([0, 0]);
        }}
      >
        <span className={'txt-underline gSemiBold'}>
          {activeLanguage
            ? activeLanguage?.reservation?.clearSelection
            : 'Clear Selection'}
        </span>
      </Button>
      {showSelectedDate && (
        <SelectedDatesPanel
          arrival={toUTCDate(selectedRanges?.[0]?.startDate)}
          departure={toUTCDate(selectedRanges?.[0]?.endDate)}
          className={'mg-bottom-25'}
        />
      )}
    </>
  );
}

export default AvailabilityCalendar;
